/**
 * Render a list of projects.
 */
import {plural} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {LabelLarge, LabelSmall} from "@khanacademy/wonder-blocks-typography";

import type {ProgramSimple} from "#scratchpad-shared-package/types.ts";

type ProjectProps = {
    project: ProgramSimple;
};

export class Project extends React.Component<ProjectProps> {
    renderScreenshot = (project: ProgramSimple): React.ReactElement => {
        return (
            <View>
                <a href={project.url} target="_blank">
                    <img
                        className={css(styles.screenshot)}
                        alt=""
                        src={project.thumb}
                    />
                </a>
                <Link href={project.url}>
                    <LabelLarge style={styles.title}>
                        {project.translatedTitle}
                    </LabelLarge>
                </Link>
            </View>
        );
    };

    renderAuthorName = (project: ProgramSimple): React.ReactElement => {
        if (!project.authorKaid) {
            // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'ReactElement<any, string | JSXElementConstructor<any>>'.
            return project.authorNickname;
        }

        const profileURL = `/profile/${project.authorKaid}`;

        return (
            <Link href={profileURL}>
                <LabelSmall style={styles.author}>
                    {project.authorNickname}
                </LabelSmall>
            </Link>
        );
    };

    renderDetails = (project: ProgramSimple): React.ReactElement => {
        return (
            <View style={styles.stats}>
                {plural(project.sumVotesIncremented, {
                    one: "# Vote",
                    other: "# Votes",
                })}{" "}
                ·{" "}
                {plural(project.spinoffCount, {
                    one: "# Spin-Off",
                    other: "# Spin-Offs",
                })}
            </View>
        );
    };

    render(): React.ReactNode {
        const project = this.props.project;

        return (
            <View style={styles.project}>
                {this.renderScreenshot(project)}
                {this.renderAuthorName(project)}
                {this.renderDetails(project)}
            </View>
        );
    }
}

type ProjectListProps = {
    projects: ReadonlyArray<ProgramSimple>;
};

class ProjectList extends React.Component<ProjectListProps> {
    render(): React.ReactNode {
        const projects = this.props.projects.map((project, i) => {
            return <Project key={project.url} project={project} />;
        });

        return <div className={css(styles.module)}>{projects}</div>;
    }
}

const ellipsis = {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    width: 200,
} as const;

const styles = StyleSheet.create({
    module: {
        overflow: "hidden",
    },

    project: {
        float: "left",
        marginBottom: 22,
        marginRight: 32,
    },

    screenshot: {
        border: `1px solid ${color.offBlack16}`,
        boxSizing: "border-box",
        height: 200,
        width: 200,
    },

    title: {
        ...ellipsis,
    },

    author: {
        ...ellipsis,
    },

    stats: {
        color: color.offBlack64,
    },
});

export default ProjectList;
